import type { ICarLeasingAdView } from "@finn-no/adview-provider-frontend-types";
import { isPrivateAdvertiser } from "@mobility-item-page/utilities";
import { AdPlacement } from "@schibsted-nmp/advertising-companion";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import { useAdViewMeta } from "../../context/AdViewProviderContext.tsx";
import type { AdProps } from "../components/Ad.tsx";
import AdReport from "../components/AdReport.tsx";
import { AdvertisingContainer } from "../components/advertising/AdvertisingPlacement.tsx";
import { DesktopCompanyProfile, EnabledDealerOverview, MobileCTAs, NoDealerBorder } from "../components/Dealer.tsx";
import Description from "../components/Description.tsx";
import Disclaimer from "../components/Disclaimer.tsx";
import { Equipment } from "../components/Equipment.tsx";
import { FeedbackForm } from "../components/FeedbackForm.tsx";
import { FinanceSection } from "../components/finance-section/FinanceSection.tsx";
import { Layout } from "../components/Layout.tsx";
import LeasingInfo from "../components/LeasingInfo.tsx";
import LocationSection from "../components/location/LocationSection.tsx";
import Price from "../components/Price.tsx";
import { PrivateSeller } from "../components/PrivateSeller.tsx";
import SearchLinks from "../components/SearchLinks.tsx";
import SafetyElements from "../components/safety-elements/Index.tsx";
import { Summary } from "../components/section-summary/SectionSummary.tsx";
import { CarSpecifications } from "../components/specification/car/CarSpecifications.tsx";
import Title from "../components/Title.tsx";
import UsefulResources from "../components/UsefulResources.tsx";
import { SignupAlert } from "../components/user-panel-signup/SignupAlert.tsx";

type Props = AdProps & {
  ad: ICarLeasingAdView;
};

export const CarLeasing = ({ ad, safetyElements }: Props) => {
  const { ownerUrn } = useAdViewMeta();
  const { isMobile, enableFeedback } = useAppConfig();
  const isPrivateAd = isPrivateAdvertiser(ownerUrn);

  return (
    <Layout
      left={
        <>
          <Title title={ad.title} subTitle={ad.model_spec} statusText={ad.disposed_text} />
          <Summary ad={ad} />
          <Price />
          <MobileCTAs />
        </>
      }
      leftBottom={
        <>
          <FinanceSection />
          <Description
            description={ad.description_unsafe}
            loanWarningLink={ad.loan_warning_link}
            carOfTheYear={ad.usedCarOfTheYear}
            carModelTaxonomy={ad.car_model_taxonomy}
            year={ad.year}
            modelSpec={ad.model_spec}
            fuelType={ad.engine?.fuel}
          />
          <LeasingInfo leasingDetails={ad.leasing_details} />
          {isMobile && <AdvertisingContainer placement={AdPlacement.AdvtMiddle2} className="mt-40" />}
          <CarSpecifications ad={ad} />
          <Equipment equipment={ad.equipment} />
          <SafetyElements safetyElements={safetyElements} />
          <LocationSection />
          {isMobile && <AdvertisingContainer placement={AdPlacement.AdvtMiddle2} className="mt-40" />}
          {isMobile && <UsefulResources />}
          {isMobile && <SignupAlert adViewType={ad.adViewType} />}
          <NoDealerBorder />
        </>
      }
      right={
        <>
          {isPrivateAd && <PrivateSeller adviewtype={ad.adViewType} />}
          <DesktopCompanyProfile />
          {!isMobile && <UsefulResources />}
          {!isMobile && <SignupAlert adViewType={ad.adViewType} />}
          {!isMobile && <AdvertisingContainer placement={AdPlacement.AdvtMiddle1} className={"mt-24"} />}
          {!isMobile && <AdvertisingContainer placement={AdPlacement.AdvtArticle1} className={"mt-24"} />}
          {!isMobile && <AdvertisingContainer placement={AdPlacement.AdvtArticle2} className={"mt-24"} />}
        </>
      }
      bottomWide={
        <>
          <EnabledDealerOverview />
          <AdReport />
          <Disclaimer />
          <SearchLinks />
          {enableFeedback && <FeedbackForm />}
        </>
      }
    />
  );
};
